import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import {
  groupExpectations,
} from "./sessionExpectations";
import AdminOnboarding, {
  isOnboardingDismissed,
  isOnboardingSetupComplete,
} from "../AdminOnboarding";
import ClubLifeHomeSection from "../common/ClubLifeHomeSection";
import SwipeCarousel from "./SwipeCarousel";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import { HubSheet } from "../AdminPortal/HubPwa";
import s from "../clinic.module.css";
import { Button, EmptyState, SkeletonCard, useToast } from "../../ui";
import {
  IconCalendar,
  IconFinance,
  IconJournal,
  IconPlus,
  IconUsers,
} from "../../ui/icons";
import L from "../../../constants/baysportLabels";
import { journalUrl, getClubFeatures } from "../../../utils/adminNav";
import { TRIAL_DAYS } from "../../../constants/trial";
import { sessionAccentStyle } from "../../../utils/clientSessionUtils";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Доброе утро";
  if (h < 18) return "Добрый день";
  return "Добрый вечер";
}

function fmtMoney(n) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

function TrialSubscriptionBanner({ billing, appMode, isOwner }) {
  if (!billing || !isOwner) return null;
  if (!billing.is_paid) return null;

  const daysLeft = billing.days_left;
  const showTrial = billing.is_trial && daysLeft != null && daysLeft >= 0;
  const showSoon = billing.is_expiring_soon && !billing.is_trial;

  if (!showTrial && !showSoon) return null;

  const tone = showSoon ? aa.trialBannerWarn : aa.trialBanner;

  return (
    <section className={`${tone} ${appMode ? aa.trialBannerApp : ""}`}>
      <div className={aa.trialBannerBody}>
        <strong>
          {showTrial
            ? `Демо-доступ: осталось ${daysLeft} из ${billing.trial_days || TRIAL_DAYS} дн.`
            : `Подписка заканчивается через ${daysLeft} дн.`}
        </strong>
        <p>
          {showTrial
            ? "После окончания демо без оплаты доступ к CRM будет ограничен."
            : "Продлите подписку, чтобы не потерять доступ к журналу и финансам."}
        </p>
      </div>
      <Link to="/manage/app/profile" className={aa.trialBannerLink}>
        Подписка →
      </Link>
    </section>
  );
}

function sessionDateBadge(sess) {
  if (sess.is_today || sess.date_label === "сегодня") return "Сегодня";
  const sessDay =
    sess.session_date ||
    (sess.start_datetime ? String(sess.start_datetime).slice(0, 10) : "");
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().slice(0, 10);
  if (sessDay === tomorrowStr) return "Завтра";
  if (sess.session_date) {
    return formatDateRu(`${sess.session_date}T12:00:00`, {
      day: "numeric",
      month: "long",
    });
  }
  return formatDateRu(sess.start_datetime, { day: "numeric", month: "long" });
}

function trainerLine(sess, profile, club) {
  const name = sess.trainer?.user?.full_name;
  const trainerUserId = sess.trainer?.user?.id;
  if (!name && !trainerUserId) return null;
  const solo = getClubFeatures(club).isSolo;
  if (profile?.id && trainerUserId === profile.id) {
    return solo ? "Вы" : null;
  }
  return name || null;
}

function sessionProgress(present, expected) {
  if (!expected) return 0;
  return Math.min(100, Math.round((present / expected) * 100));
}

function isSessionNow(sess) {
  const now = Date.now();
  const start = new Date(sess.start_datetime).getTime();
  const end = new Date(sess.end_datetime).getTime();
  return now >= start && now <= end;
}

function isSessionPast(sess) {
  if (sess.status === "completed") return true;
  if (!sess.end_datetime) return false;
  return new Date(sess.end_datetime).getTime() < Date.now();
}

/** Скрываем с главной занятия, которые закончились более 2 ч назад. */
const HOME_SESSION_GRACE_MS = 2 * 60 * 60 * 1000;

function sessionDayKey(sess) {
  if (sess.session_date) return sess.session_date;
  if (sess.start_datetime) return String(sess.start_datetime).slice(0, 10);
  return "";
}

/** Все ближайшие занятия (тренировки и соревнования), без ограничения одним днём. */
function filterHomeSessions(sessions) {
  const cutoff = Date.now() - HOME_SESSION_GRACE_MS;
  return (sessions || [])
    .filter((sess) => sess.status !== "cancelled" && sess.status !== "completed")
    .filter((sess) => {
      const endMs = sess.end_datetime
        ? new Date(sess.end_datetime).getTime()
        : null;
      if (!endMs) return true;
      return endMs >= cutoff;
    })
    .sort(
      (a, b) =>
        new Date(a.start_datetime).getTime() -
        new Date(b.start_datetime).getTime(),
    )
    .slice(0, 8);
}

function adminSessionAccent(sess) {
  const isCompetition =
    sess.session_kind === "event" && sess.event_kind === "competition";
  if (isCompetition) {
    return { "--admin-slot-accent": "#d97706" };
  }
  return sessionAccentStyle(sess, "--admin-slot-accent");
}

function sessionCardTitle(sess) {
  const isCompetition =
    sess.session_kind === "event" && sess.event_kind === "competition";
  if (isCompetition) return sess.title || "Соревнование";
  return sess.group?.name || sess.title || "Занятие";
}

const ATTENTION_NOTIFICATION_TYPES = new Set([
  "unpaid_membership",
  "debtors",
  "session_unfinished",
  "medical_expired",
  "competition_application_pending",
]);

/** Только задачи, требующие действия — без выручки, «давно не был» и прочих info. */
function filterAttentionNotifications(notifications) {
  return (notifications || []).filter((n) => {
    if (n.type === "revenue" || n.type === "absent_long") return false;
    if (ATTENTION_NOTIFICATION_TYPES.has(n.type)) return true;
    if (n.type === "membership_expiring" || n.type === "medical_expiring") {
      return n.tone === "warn" || n.tone === "danger";
    }
    return false;
  });
}

function peopleCountLabel(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} человек`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `${n} человека`;
  }
  return `${n} человек`;
}

function ExpectationRespondentsSheet({ open, onClose, title, rows, columnKey }) {
  const [expandedId, setExpandedId] = useState(null);
  const showReasonOnTap = columnKey === "not";
  const subtitle =
    rows.length > 0 ? peopleCountLabel(rows.length) : "Ответов пока нет";

  useEffect(() => {
    if (!open) setExpandedId(null);
  }, [open]);

  return (
    <HubSheet
      open={open}
      onClose={onClose}
      title={title}
      subtitle={
        showReasonOnTap && rows.length > 0
          ? `${subtitle} · нажмите на имя, чтобы увидеть причину`
          : subtitle
      }
      sheetClassName={aa.hubSheetPeople}
      bodyClassName={aa.hubSheetPeopleBody}
    >
      {rows.length === 0 ? (
        <div className={aa.hubSheetPeopleEmpty}>
          <p className={aa.hubSheetPeopleEmptyTitle}>Список пуст</p>
          <p className={aa.hubHint}>
            Клиенты появятся здесь, когда ответят в опросе или отметят явку
          </p>
        </div>
      ) : (
        <ul className={aa.hubSheetPeopleList}>
          {rows.map((row) => {
            const note = row.note || row.absence_reason;
            const expanded = expandedId === row.patient_id;

            if (showReasonOnTap) {
              return (
                <li key={row.patient_id}>
                  <button
                    type="button"
                    className={aa.hubSheetPersonRow}
                    onClick={() =>
                      setExpandedId(expanded ? null : row.patient_id)
                    }
                    aria-expanded={expanded}
                  >
                    <span className={aa.hubSheetPersonRowBody}>
                      <span className={aa.hubSheetPersonName}>
                        {row.full_name}
                      </span>
                      {expanded ? (
                        <span className={aa.hubSheetPersonNote}>
                          {note || "Причина не указана"}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`${aa.hubSheetPersonChevron} ${
                        expanded ? aa.hubSheetPersonChevronOpen : ""
                      }`}
                      aria-hidden
                    />
                  </button>
                </li>
              );
            }

            return (
              <li key={row.patient_id}>
                <Link
                  to={`/manage/app/clients/${row.patient_id}`}
                  className={aa.hubSheetPersonRow}
                  onClick={onClose}
                >
                  <span className={aa.hubSheetPersonRowBody}>
                    <span className={aa.hubSheetPersonName}>
                      {row.full_name}
                    </span>
                    {note ? (
                      <span className={aa.hubSheetPersonNote}>{note}</span>
                    ) : null}
                  </span>
                  <span className={aa.hubSheetPersonChevron} aria-hidden />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </HubSheet>
  );
}

function SummaryForecasts({ forecasts, appMode }) {
  if (!forecasts) return null;
  const f = forecasts;
  const monthActual = f.revenue_month_actual ?? 0;
  const monthForecast = f.revenue_month_forecast ?? monthActual;
  const daysLeft = f.days_left_in_month ?? 0;
  const weekActual = f.revenue_week_last_7d ?? 0;
  const pending = f.pending_revenue ?? 0;
  const renewal = f.renewal_potential_7d ?? 0;
  const expiring = f.expiring_memberships_7d ?? 0;
  const monthDone = daysLeft === 0;
  const showMonthOutlook = !monthDone && monthActual > 0;

  const cards = [
    {
      key: "month",
      label: monthDone ? "Итог месяца" : "Доход за месяц",
      value: fmtMoney(monthActual),
      hint: monthDone
        ? "фактические поступления"
        : showMonthOutlook
          ? `≈ ${fmtMoney(monthForecast)} к концу месяца`
          : "с начала месяца",
    },
    {
      key: "week",
      label: "За 7 дней",
      value: fmtMoney(weekActual),
      hint:
        weekActual > 0
          ? "фактические поступления"
          : "платежей за неделю пока не было",
    },
    {
      key: "pending",
      label: "Ожидают оплаты",
      value: fmtMoney(pending),
      hint:
        pending > 0 ? "неоплаченные абонементы" : "все абонементы оплачены",
    },
    {
      key: "renewal",
      label: "Продления",
      value: fmtMoney(renewal),
      hint:
        expiring > 0
          ? `${expiring} абонемент(ов) истекает на неделе`
          : "на этой неделе нет",
    },
  ];

  return (
    <section
      className={`${s.summaryBlock} ${appMode ? aa.financeBlock : ""}`}
      aria-label="Финансы"
    >
      <div className={s.summaryBlockHead}>
        <h2 className={s.summaryBlockTitle}>Финансы</h2>
        <Link to="/manage/app/finance" className={s.summaryBlockMeta}>
          Подробнее →
        </Link>
      </div>
      <div className={s.summaryForecasts}>
        {cards.map((card) => (
          <div key={card.key} className={s.summaryForecastCard}>
            <span className={s.summaryForecastValue}>{card.value}</span>
            <span className={s.summaryForecastLabel}>{card.label}</span>
            <span className={s.summaryForecastHint}>{card.hint}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SummaryNotifications({ notifications, appMode }) {
  const renderNotify = (n) =>
    n.action_path ? (
      <Link
        to={n.action_path}
        className={`${s.summaryNotifyItem} ${s.summaryNotifyItemSwipe} ${s[`summaryNotify_${n.tone}`] || ""}`}
      >
        <span className={s.summaryNotifyText}>{n.text}</span>
        <span className={s.summaryNotifyAction}>
          {n.action_label || "Открыть"} →
        </span>
      </Link>
    ) : (
      <div
        className={`${s.summaryNotifyItem} ${s.summaryNotifyItemSwipe} ${s[`summaryNotify_${n.tone}`] || ""}`}
      >
        <span className={s.summaryNotifyText}>{n.text}</span>
      </div>
    );

  return (
    <section
      className={[
        s.summaryBlock,
        appMode ? aa.notifyBlock : "",
        notifications.length ? aa.notifyBlockActive : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Требует внимания"
    >
      <div className={s.summaryBlockHead}>
        <h2 className={s.summaryBlockTitle}>Требует внимания</h2>
        {notifications.length > 0 ? (
          <span className={s.summaryNotifyCount}>{notifications.length}</span>
        ) : null}
      </div>
      {notifications.length === 0 ? (
        <p className={s.summaryEmptyNote}>Всё под контролем — задач нет</p>
      ) : appMode && notifications.length > 1 ? (
        <SwipeCarousel
          items={notifications}
          getKey={(n) => n.id}
          hint="Листайте уведомления"
          ariaLabel="Уведомления"
          renderSlide={(n) => renderNotify(n)}
        />
      ) : (
        <ul className={s.summaryNotifyList}>
          {notifications.map((n) => (
            <li key={n.id}>{renderNotify(n)}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SummaryPendingRegistrations({ registrations, count, appMode }) {
  if (!count) return null;

  return (
    <section
      className={[s.summaryBlock, appMode ? aa.competitionAppsBlock : ""]
        .filter(Boolean)
        .join(" ")}
      aria-label="Заявки на регистрацию"
    >
      <div className={s.summaryBlockHead}>
        <h2 className={s.summaryBlockTitle}>Регистрация родителей</h2>
        <span className={aa.competitionAppsCount}>{count}</span>
      </div>
      <ul className={aa.competitionAppsList}>
        {(registrations || []).map((row) => (
          <li key={row.id}>
            <Link to={row.action_path} className={aa.competitionAppRow}>
              <span className={aa.competitionAppIcon} aria-hidden>
                👤
              </span>
              <span className={aa.competitionAppText}>
                <strong>{row.full_name}</strong>
                <span>{row.email || row.phone || "Без контактов"}</span>
              </span>
              <span className={aa.competitionAppAction}>Модерация →</span>
            </Link>
          </li>
        ))}
      </ul>
      {count > (registrations?.length || 0) ? (
        <Link
          to="/manage/app/clients?registration=pending"
          className={aa.analyticsActionLink}
          style={{ display: "block", marginTop: 8 }}
        >
          Все заявки ({count}) →
        </Link>
      ) : null}
    </section>
  );
}

function SummaryCompetitionApplications({ applications, appMode }) {
  if (!applications?.length) return null;

  return (
    <section
      className={[s.summaryBlock, appMode ? aa.competitionAppsBlock : ""]
        .filter(Boolean)
        .join(" ")}
      aria-label="Заявки на соревнования"
    >
      <div className={s.summaryBlockHead}>
        <h2 className={s.summaryBlockTitle}>Заявки на соревнования</h2>
        <span className={aa.competitionAppsCount}>{applications.length}</span>
      </div>
      <ul className={aa.competitionAppsList}>
        {applications.map((app) => (
          <li key={app.id}>
            <Link to={app.action_path} className={aa.competitionAppRow}>
              <span className={aa.competitionAppIcon} aria-hidden>
                🏆
              </span>
              <span className={aa.competitionAppText}>
                <strong>{app.athlete_name}</strong>
                <span>{app.competition_title}</span>
                <small>
                  {formatDateRu(app.start_datetime, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
                  {" · "}
                  {formatTimeRu(app.start_datetime)}
                </small>
              </span>
              <span className={aa.competitionAppAction}>Рассмотреть →</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SummaryQuickActions({ debtorsCount, navigate, appMode, desktop = false }) {
  const actions = [
    {
      key: "journal",
      label: "Журнал",
      icon: IconJournal,
      onClick: () => navigate("/manage/app/journal"),
    },
    {
      key: "finance",
      label: "Оплата",
      icon: IconFinance,
      onClick: () => navigate("/manage/app/finance"),
    },
    {
      key: "client",
      label: "Клиент",
      icon: IconPlus,
      onClick: () => navigate("/manage/app/clients/new"),
    },
    {
      key: "schedule",
      label: L.schedule,
      icon: IconCalendar,
      onClick: () => navigate("/manage/app/schedule"),
    },
  ];
  if (debtorsCount > 0) {
    actions.push({
      key: "debtors",
      label: `Должники · ${debtorsCount}`,
      icon: IconUsers,
      warn: true,
      onClick: () => navigate("/manage/app/finance?tab=debtors"),
    });
  }

  if (desktop) {
    return (
      <section className={md.desktopQuickBlock} aria-label="Быстрые действия">
        <h2 className={md.desktopBlockTitle}>Быстрые действия</h2>
        <div className={md.desktopQuickGrid}>
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.key}
                type="button"
                className={
                  action.warn
                    ? `${md.desktopQuickBtn} ${md.desktopQuickBtnWarn}`
                    : md.desktopQuickBtn
                }
                onClick={action.onClick}
              >
                <span className={md.desktopQuickIcon}>
                  <Icon width={18} height={18} />
                </span>
                <span>{action.label}</span>
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  if (appMode) {
    return (
      <section className={aa.homeQuickGrid} aria-label="Быстрые действия">
        <h2 className={aa.homeQuickGridTitle}>Быстрые действия</h2>
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.key}
              type="button"
              className={
                action.warn
                  ? `${aa.homeQuickTile} ${aa.homeQuickTileWarn}`
                  : aa.homeQuickTile
              }
              onClick={action.onClick}
            >
              <span className={aa.homeQuickTileIcon}>
                <Icon width={20} height={20} />
              </span>
              <span className={aa.homeQuickTileLabel}>{action.label}</span>
            </button>
          );
        })}
      </section>
    );
  }

  return (
    <section
      className={`${s.summaryBlock} ${appMode ? aa.quickActionsBlock : ""}`}
    >
      <h2 className={s.summaryBlockTitle}>Быстрые действия</h2>
      <div className={s.summaryActions}>
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.key}
              type="button"
              className={
                action.warn
                  ? `${s.summaryActionBtn} ${s.summaryActionBtn_warn}`
                  : s.summaryActionBtn
              }
              onClick={action.onClick}
            >
              <Icon width={appMode ? 20 : 20} height={appMode ? 20 : 20} />
              <span>{action.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SummaryNearestSessions({
  sessions,
  navigate,
  profile,
  club,
  openJournal,
  desktop = false,
}) {
  const visible = filterHomeSessions(sessions);
  const focus = visible[0];
  const rest = visible.slice(1);

  if (desktop) {
    if (!visible.length) {
      return (
        <section className={md.desktopNearestBlock} aria-label="Ближайшие занятия">
          <div className={md.desktopNearestHead}>
            <h2 className={md.desktopNearestTitle}>Ближайшие занятия</h2>
            <Link to="/manage/app/schedule" className={md.desktopNearestLink}>
              Календарь
            </Link>
          </div>
          <EmptyState
            title="Ближайших занятий нет"
            description="Добавьте расписание группы — тренировки появятся здесь"
            actionLabel="Открыть расписание"
            onAction={() => navigate("/manage/app/schedule")}
          />
        </section>
      );
    }

    return (
      <section className={md.desktopNearestBlock} aria-label="Ближайшие занятия">
        <div className={md.desktopNearestHead}>
          <h2 className={md.desktopNearestTitle}>Ближайшие занятия</h2>
          <Link to="/manage/app/schedule" className={md.desktopNearestLink}>
            Календарь
          </Link>
        </div>

        <AdminHomeSessionCard
          sess={focus}
          profile={profile}
          club={club}
          onOpenJournal={() => openJournal(focus)}
          expectSheet
          desktop
        />

        {rest.length > 0 ? (
          <div className={md.desktopUpcoming}>
            <p className={md.desktopUpcomingLabel}>Ещё в расписании</p>
            <div className={md.desktopUpcomingGrid}>
              {rest.map((sess) => {
                const isCompetition =
                  sess.session_kind === "event" &&
                  sess.event_kind === "competition";
                return (
                  <button
                    key={sess.id}
                    type="button"
                    className={
                      isCompetition
                        ? `${md.desktopUpcomingChip} ${md.desktopUpcomingChipCompetition}`
                        : md.desktopUpcomingChip
                    }
                    onClick={() => openJournal(sess)}
                  >
                    <span className={md.desktopUpcomingChipDay}>
                      {formatDateRu(sess.start_datetime, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                    <span className={md.desktopUpcomingChipTime}>
                      {formatTimeRu(sess.start_datetime)}
                    </span>
                    <strong className={md.desktopUpcomingChipName}>
                      {sessionCardTitle(sess)}
                    </strong>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
    );
  }

  const blockClass = [s.summaryBlock, aa.nearestBlock].join(" ");

  if (!visible.length) {
    return (
      <section
        className={blockClass}
        aria-label="Ближайшие занятия"
      >
        <div className={s.summaryBlockHead}>
          <h2 className={s.summaryBlockTitle}>Ближайшие занятия</h2>
          <Link to="/manage/app/schedule" className={aa.nearestCalendarLink}>
            Календарь
          </Link>
        </div>
        <div className={s.summaryBlockBody}>
          <EmptyState
            title="Ближайших занятий нет"
            description="Добавьте расписание группы — тренировки появятся здесь"
            actionLabel="Открыть расписание"
            onAction={() => navigate("/manage/app/schedule")}
          />
        </div>
      </section>
    );
  }

  return (
    <section
      className={blockClass}
      aria-label="Ближайшие занятия"
    >
      <div className={s.summaryBlockHead}>
        <h2 className={s.summaryBlockTitle}>Ближайшие занятия</h2>
        <Link to="/manage/app/schedule" className={aa.nearestCalendarLink}>
          Календарь
        </Link>
      </div>

      <AdminHomeSessionCard
        sess={focus}
        profile={profile}
        club={club}
        onOpenJournal={() => openJournal(focus)}
        expectSheet
      />

      {rest.length > 0 ? (
        <div className={aa.adminUpcomingStrip}>
          <p className={aa.adminUpcomingStripLabel}>Ещё в расписании</p>
          <div className={aa.adminUpcomingStripRow}>
            {rest.map((sess) => {
              const isCompetition =
                sess.session_kind === "event" &&
                sess.event_kind === "competition";
              return (
                <button
                  key={sess.id}
                  type="button"
                  className={`${aa.adminUpcomingChip} ${
                    isCompetition ? aa.adminUpcomingChipCompetition : ""
                  }`}
                  onClick={() => openJournal(sess)}
                >
                  <span className={aa.adminUpcomingChipDay}>
                    {formatDateRu(sess.start_datetime, {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                  <span className={aa.adminUpcomingChipTime}>
                    {formatTimeRu(sess.start_datetime)}
                  </span>
                  <strong className={aa.adminUpcomingChipName}>
                    {sessionCardTitle(sess)}
                  </strong>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function KpiCard({ label, value, hint, accent, icon: Icon, appMode }) {
  if (appMode) {
    return (
      <article
        className={
          accent
            ? `${aa.homeKpiCard} ${aa.homeKpiCardAccent}`
            : aa.homeKpiCard
        }
      >
        {Icon ? (
          <span className={aa.homeKpiIcon} aria-hidden>
            <Icon width={20} height={20} />
          </span>
        ) : null}
        <span className={aa.homeKpiValue}>{value}</span>
        <span className={aa.homeKpiLabel}>{label}</span>
        {hint ? <span className={aa.homeKpiHint}>{hint}</span> : null}
      </article>
    );
  }

  return (
    <div
      className={
        accent
          ? `${s.summaryKpi} ${s.summaryKpi_accent}`
          : s.summaryKpi
      }
    >
      {Icon ? (
        <span className={s.summaryKpiIcon} aria-hidden>
          <Icon width={20} height={20} />
        </span>
      ) : null}
      <span className={s.summaryKpiValue}>{value}</span>
      <span className={s.summaryKpiLabel}>{label}</span>
      {hint ? <span className={s.summaryKpiHint}>{hint}</span> : null}
    </div>
  );
}

function FeaturedExpectations({
  expectations,
  summary,
  expectedCount = 0,
  onColumnOpen,
}) {
  const groups = groupExpectations(expectations);
  const total = Math.max(
    Number(summary?.total) || 0,
    expectations?.length || 0,
    Number(expectedCount) || 0,
  );

  const effectiveSummary = summary?.total
    ? summary
    : {
        coming: groups.coming.length,
        not_coming: groups.notComing.length,
        unknown: 0,
        pending: Math.max(
          0,
          total - groups.coming.length - groups.notComing.length,
        ),
        total,
      };

  const pendingCount =
    (effectiveSummary.pending || 0) + (effectiveSummary.unknown || 0);
  const columns = [
    {
      key: "coming",
      tone: "coming",
      count: effectiveSummary.coming ?? groups.coming.length,
      label: "придут",
      rows: groups.coming,
    },
    {
      key: "not",
      tone: "not",
      count: effectiveSummary.not_coming ?? groups.notComing.length,
      label: "не придут",
      rows: groups.notComing,
    },
    {
      key: "pending",
      tone: "pending",
      count: pendingCount || groups.pending.length,
      label: "без ответа",
      rows: groups.pending,
    },
  ];

  return (
    <div className={s.featuredExpect}>
      <div className={s.featuredExpectGrid}>
        {columns.map((col) => {
          const ColTag = onColumnOpen ? "button" : "div";
          return (
            <ColTag
              key={col.key}
              type={onColumnOpen ? "button" : undefined}
              className={`${s.featuredExpectCol} ${s[`featuredExpectCol_${col.tone}`]} ${
                onColumnOpen ? s.featuredExpectColBtn : ""
              }`}
              onClick={onColumnOpen ? () => onColumnOpen(col) : undefined}
              aria-label={
                onColumnOpen ? `Открыть список: ${col.label}` : undefined
              }
            >
              <div className={s.featuredExpectColHead}>
                <strong>{col.count}</strong>
                <span>{col.label}</span>
              </div>
              {col.rows.length > 0 ? (
                <ul className={s.featuredExpectNames}>
                  {col.rows.slice(0, 2).map((row) => (
                    <li key={row.patient_id} title={row.full_name}>
                      {row.full_name}
                    </li>
                  ))}
                  {col.rows.length > 2 ? (
                    <li className={s.featuredExpectMore}>
                      +{col.rows.length - 2}
                    </li>
                  ) : null}
                </ul>
              ) : (
                <span className={s.featuredExpectEmpty}>—</span>
              )}
            </ColTag>
          );
        })}
      </div>
      {onColumnOpen ? (
        <p className={s.featuredExpectTapHint}>Нажмите на колонку — откроется список</p>
      ) : null}
    </div>
  );
}

function FeaturedCompetitionRegs({
  summary,
  registrations,
  onColumnOpen,
}) {
  const approved = summary?.approved ?? 0;
  const pending = summary?.pending ?? 0;
  const rejected = summary?.rejected ?? 0;
  const approvedRows = (registrations || []).filter((r) => r.status === "approved");
  const pendingRows = (registrations || []).filter((r) => r.status === "pending");
  const rejectedRows = (registrations || []).filter((r) => r.status === "rejected");

  const columns = [
    {
      key: "approved",
      tone: "coming",
      count: approved,
      label: "допущено",
      rows: approvedRows.map((r) => ({
        patient_id: r.patient_id,
        full_name: r.athlete?.full_name || "Участник",
      })),
    },
    {
      key: "pending",
      tone: "pending",
      count: pending,
      label: "заявок",
      rows: pendingRows.map((r) => ({
        patient_id: r.patient_id,
        full_name: r.athlete?.full_name || "Участник",
      })),
    },
    {
      key: "rejected",
      tone: "not",
      count: rejected,
      label: "не допущен",
      rows: rejectedRows.map((r) => ({
        patient_id: r.patient_id,
        full_name: r.athlete?.full_name || "Участник",
      })),
    },
  ];

  return (
    <div className={`${s.featuredExpect} ${aa.featuredCompetitionExpect}`}>
      <div className={`${s.featuredExpectGrid} ${aa.featuredCompetitionGrid}`}>
        {columns.map((col) => {
          const ColTag = onColumnOpen ? "button" : "div";
          return (
            <ColTag
              key={col.key}
              type={onColumnOpen ? "button" : undefined}
              className={`${s.featuredExpectCol} ${s[`featuredExpectCol_${col.tone}`]} ${
                onColumnOpen ? s.featuredExpectColBtn : ""
              }`}
              onClick={onColumnOpen ? () => onColumnOpen(col) : undefined}
              aria-label={
                onColumnOpen ? `Открыть список: ${col.label}` : undefined
              }
            >
              <div className={s.featuredExpectColHead}>
                <strong>{col.count}</strong>
                <span>{col.label}</span>
              </div>
              {col.rows.length > 0 ? (
                <ul className={s.featuredExpectNames}>
                  {col.rows.slice(0, 2).map((row) => (
                    <li key={row.patient_id} title={row.full_name}>
                      {row.full_name}
                    </li>
                  ))}
                  {col.rows.length > 2 ? (
                    <li className={s.featuredExpectMore}>
                      +{col.rows.length - 2}
                    </li>
                  ) : null}
                </ul>
              ) : (
                <span className={s.featuredExpectEmpty}>—</span>
              )}
            </ColTag>
          );
        })}
      </div>
      {onColumnOpen ? (
        <p className={s.featuredExpectTapHint}>Нажмите на колонку — откроется список</p>
      ) : null}
    </div>
  );
}

function AdminHomeSessionCard({
  sess,
  onOpenJournal,
  profile,
  club,
  expectSheet = false,
  desktop = false,
}) {
  const [expectSheetState, setExpectSheetState] = useState(null);
  const expected = sess.expected_count ?? sess.attendees?.length ?? 0;
  const present = sess.present_count ?? 0;
  const trainer = trainerLine(sess, profile, club);
  const canMark = sess.status !== "cancelled";
  const expectations = sess.expectations || [];
  const summary = sess.expectation_summary;
  const isCompetition =
    sess.session_kind === "event" && sess.event_kind === "competition";
  const isTraining =
    !isCompetition &&
    sess.session_kind !== "event" &&
    Boolean(sess.group?.id || sess.group?.name || sess.specialization_id);
  const showPollExpectations =
    canMark &&
    !isCompetition &&
    (isTraining || (summary?.total ?? 0) > 0 || expectations.length > 0);
  const competitionSummary = sess.competition_registration_summary;
  const competitionRegs = sess.competition_registrations || [];
  const sessionTitle = sessionCardTitle(sess);
  const now = isSessionNow(sess);
  const past = !now && isSessionPast(sess);
  const accentStyle = adminSessionAccent(sess);

  const typeBadge = isCompetition ? (
    <span className={md.desktopSessionBadge}>Соревнование</span>
  ) : (
    <span className={md.desktopSessionBadgeTraining}>Тренировка</span>
  );

  const competitionBlock =
    canMark && isCompetition ? (
      <FeaturedCompetitionRegs
        summary={competitionSummary}
        registrations={competitionRegs}
        onColumnOpen={
          expectSheet
            ? (col) =>
                setExpectSheetState({
                  title:
                    col.key === "approved"
                      ? "Допущены"
                      : col.key === "pending"
                        ? "Заявки на рассмотрении"
                        : "Не допущены",
                  rows: col.rows,
                  columnKey: col.key,
                })
            : undefined
        }
      />
    ) : null;

  const pollExpectBlock =
    showPollExpectations ? (
      <div className={md.desktopExpect}>
        <FeaturedExpectations
          expectations={expectations}
          summary={summary}
          expectedCount={expected}
          onColumnOpen={
            expectSheet
              ? (col) =>
                  setExpectSheetState({
                    title:
                      col.key === "coming"
                        ? "Придут"
                        : col.key === "not"
                          ? "Не придут"
                          : "Без ответа",
                    rows: col.rows,
                    columnKey: col.key,
                  })
              : undefined
          }
        />
      </div>
    ) : null;

  const trainingHint =
    canMark && isTraining ? (
      <p className={md.desktopSessionHint}>
        {past
          ? present > 0
            ? `Перекличка ${present}/${expected}`
            : "Перекличка в журнале"
          : `${expected} в группе${
              summary?.coming
                ? ` · ${summary.coming} придут`
                : present > 0
                  ? ` · перекличка ${present}/${expected}`
                  : ""
            }`}
      </p>
    ) : null;

  const competitionHint =
    canMark && isCompetition ? (
      <p className={md.desktopSessionHint}>
        {(competitionSummary?.total ?? 0) + (competitionSummary?.rejected ?? 0) > 0
          ? `${competitionSummary.approved} допущено${
              competitionSummary.pending > 0
                ? ` · ${competitionSummary.pending} заявок`
                : ""
            }${
              competitionSummary.rejected > 0
                ? ` · ${competitionSummary.rejected} не допущен`
                : ""
            }`
          : "Заявок на соревнование пока нет"}
      </p>
    ) : null;

  const expectSheetModal = expectSheet ? (
    <ExpectationRespondentsSheet
      open={Boolean(expectSheetState)}
      onClose={() => setExpectSheetState(null)}
      title={expectSheetState?.title || ""}
      rows={expectSheetState?.rows || []}
      columnKey={expectSheetState?.columnKey}
    />
  ) : null;

  if (desktop) {
    return (
      <article
        className={[
          md.desktopSessionCard,
          now ? md.desktopSessionCardNow : "",
          past ? md.desktopSessionCardPast : "",
          isCompetition ? md.desktopSessionCardCompetition : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={accentStyle}
      >
        <div className={md.desktopSessionTimeCol}>
          <span className={md.desktopSessionTime}>
            {formatTimeRu(sess.start_datetime)}
          </span>
          <span className={md.desktopSessionDate}>
            {sessionDateBadge(sess)}
            {now ? (
              <em className={md.desktopSessionLive}> · сейчас</em>
            ) : past ? (
              <em className={md.desktopSessionPastTag}> · прошло</em>
            ) : null}
          </span>
        </div>

        <div className={md.desktopSessionMain}>
          <h3 className={md.desktopSessionName}>{sessionTitle}</h3>
          <p className={md.desktopSessionMeta}>
            {[
              sess.branch?.name || "Зал не указан",
              trainer ? `Тренер: ${trainer}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {!canMark ? (
            <p className={s.summaryAttendEmpty}>Занятие отменено</p>
          ) : null}
          {competitionBlock}
          {pollExpectBlock}
          {trainingHint}
          {competitionHint}
        </div>

        <div className={md.desktopSessionActions}>
          {typeBadge}
          <Button
            type="button"
            variant={past ? "secondary" : "primary"}
            size="sm"
            onClick={onOpenJournal}
            className={md.desktopSessionBtn}
          >
            {L.openJournal}
          </Button>
        </div>

        {expectSheetModal}
      </article>
    );
  }

  return (
    <article
      className={[
        aa.adminHomeSession,
        now ? aa.adminHomeSessionNow : "",
        past ? aa.adminHomeSessionPast : "",
        isCompetition ? aa.adminHomeSessionCompetition : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={accentStyle}
    >
      <div className={aa.adminHomeSessionBody}>
        <div className={aa.adminHomeSessionTop}>
          <div>
            <span className={aa.adminHomeSessionTime}>
              {formatTimeRu(sess.start_datetime)}
            </span>
            <span className={aa.adminHomeSessionDate}>
              {sessionDateBadge(sess)}
              {now ? (
                <em className={aa.adminHomeSessionLive}> · сейчас</em>
              ) : past ? (
                <em className={aa.adminHomeSessionPastTag}> · прошло</em>
              ) : null}
            </span>
          </div>
          {isCompetition ? (
            <span className={aa.adminHomeSessionTypeBadge}>Соревнование</span>
          ) : (
            <span className={aa.adminHomeSessionTypeBadgeTraining}>
              Тренировка
            </span>
          )}
        </div>

        <h3 className={aa.adminHomeSessionName}>{sessionTitle}</h3>
        <p className={aa.adminHomeSessionMeta}>
          {[
            sess.branch?.name || "Зал не указан",
            trainer ? `Тренер: ${trainer}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>

        {!canMark ? (
          <p className={s.summaryAttendEmpty}>Занятие отменено</p>
        ) : null}

        {canMark && isCompetition ? (
          <FeaturedCompetitionRegs
            summary={competitionSummary}
            registrations={competitionRegs}
            onColumnOpen={
              expectSheet
                ? (col) =>
                    setExpectSheetState({
                      title:
                        col.key === "approved"
                          ? "Допущены"
                          : col.key === "pending"
                            ? "Заявки на рассмотрении"
                            : "Не допущены",
                      rows: col.rows,
                      columnKey: col.key,
                    })
                : undefined
            }
          />
        ) : null}

        {showPollExpectations ? (
          <FeaturedExpectations
            expectations={expectations}
            summary={summary}
            expectedCount={expected}
            onColumnOpen={
              expectSheet
                ? (col) =>
                    setExpectSheetState({
                      title:
                        col.key === "coming"
                          ? "Придут"
                          : col.key === "not"
                            ? "Не придут"
                            : "Без ответа",
                      rows: col.rows,
                      columnKey: col.key,
                    })
                : undefined
            }
          />
        ) : null}

        {canMark && isTraining ? (
          <p className={aa.adminHomeSessionHint}>
            {past
              ? present > 0
                ? `Перекличка ${present}/${expected}`
                : "Перекличка в журнале"
              : `${expected} в группе${
                  summary?.coming
                    ? ` · ${summary.coming} придут`
                    : present > 0
                      ? ` · перекличка ${present}/${expected}`
                      : ""
                }`}
          </p>
        ) : null}

        {canMark && isCompetition ? (
          <p className={aa.adminHomeSessionHint}>
            {(competitionSummary?.total ?? 0) + (competitionSummary?.rejected ?? 0) > 0
              ? `${competitionSummary.approved} допущено${
                  competitionSummary.pending > 0
                    ? ` · ${competitionSummary.pending} заявок`
                    : ""
                }${
                  competitionSummary.rejected > 0
                    ? ` · ${competitionSummary.rejected} не допущен`
                    : ""
                }`
              : "Заявок на соревнование пока нет"}
          </p>
        ) : null}
      </div>

      <Button
        type="button"
        variant={past ? "secondary" : "primary"}
        onClick={onOpenJournal}
        className={aa.adminHomeSessionBtn}
      >
        {L.openJournal}
      </Button>

      {expectSheetModal}
    </article>
  );
}

function FeaturedSession({
  sess,
  onOpenJournal,
  profile,
  club,
  expectSheet = false,
}) {
  const [expectSheetState, setExpectSheetState] = useState(null);
  const expected = sess.expected_count ?? sess.attendees?.length ?? 0;
  const present = sess.present_count ?? 0;
  const trainer = trainerLine(sess, profile, club);
  const canMark = sess.status !== "cancelled";
  const expectations = sess.expectations || [];
  const summary = sess.expectation_summary;
  const isCompetition =
    sess.session_kind === "event" && sess.event_kind === "competition";
  const isTraining =
    !isCompetition &&
    sess.session_kind !== "event" &&
    Boolean(sess.group?.id || sess.group?.name || sess.specialization_id);
  const showPollExpectations =
    canMark &&
    !isCompetition &&
    (isTraining || (summary?.total ?? 0) > 0 || expectations.length > 0);
  const competitionSummary = sess.competition_registration_summary;
  const competitionRegs = sess.competition_registrations || [];
  const sessionTitle = isCompetition
    ? sess.title || "Соревнование"
    : sess.group?.name;
  const now = isSessionNow(sess);
  const past = !now && isSessionPast(sess);

  return (
    <article
      className={[
        s.summaryFeatured,
        now ? s.summaryFeatured_now : "",
        past ? s.summaryFeatured_past : "",
        isCompetition ? aa.summaryFeaturedCompetition : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={s.summaryFeaturedHead}>
        <span
          className={
            isCompetition
              ? aa.summaryFeaturedBadgeCompetition
              : s.summaryFeaturedBadge
          }
        >
          {isCompetition ? "Соревнование" : sessionDateBadge(sess)}
        </span>
        {now ? (
          <span className={s.summaryLiveBadge}>сейчас</span>
        ) : past ? (
          <span className={s.summaryPastBadge}>прошло</span>
        ) : null}
      </div>
      <p className={s.summaryFeaturedTime}>
        {formatTimeRu(sess.start_datetime)}
        <span className={s.summaryFeaturedTimeSep}>—</span>
        {formatTimeRu(sess.end_datetime)}
      </p>
      <h3 className={s.summaryFeaturedTitle}>
        {sessionTitle}
        {!isCompetition && sess.group?.sport_direction ? (
          <span className={s.summaryFeaturedDir}>
            {sess.group.sport_direction}
          </span>
        ) : null}
      </h3>
      <ul className={s.summaryFeaturedMeta}>
        <li>{sess.branch?.name || "Зал не указан"}</li>
        {trainer ? <li>Тренер: {trainer}</li> : null}
      </ul>

      {!canMark ? (
        <p className={s.summaryAttendEmpty}>Занятие отменено</p>
      ) : null}

      {canMark && isCompetition ? (
        <FeaturedCompetitionRegs
          summary={competitionSummary}
          registrations={competitionRegs}
          onColumnOpen={
            expectSheet
              ? (col) =>
                  setExpectSheetState({
                    title:
                      col.key === "approved"
                        ? "Допущены"
                        : col.key === "pending"
                          ? "Заявки на рассмотрении"
                          : col.key === "rejected"
                            ? "Не допущены"
                            : "Заявки на рассмотрении",
                    rows: col.rows,
                    columnKey: col.key,
                  })
              : undefined
          }
        />
      ) : null}

      {showPollExpectations ? (
        <FeaturedExpectations
          expectations={expectations}
          summary={summary}
          expectedCount={expected}
          onColumnOpen={
            expectSheet
              ? (col) =>
                  setExpectSheetState({
                    title:
                      col.key === "coming"
                        ? "Придут"
                        : col.key === "not"
                          ? "Не придут"
                          : "Без ответа",
                    rows: col.rows,
                    columnKey: col.key,
                  })
              : undefined
          }
        />
      ) : null}

      {canMark && isTraining ? (
        <p className={s.featuredSessionHint}>
          {past
            ? present > 0
              ? `Занятие прошло · перекличка ${present}/${expected}`
              : "Занятие прошло · перекличка в журнале"
            : `${expected} в группе${
                present > 0
                  ? ` · перекличка ${present}/${expected}`
                  : summary?.coming
                    ? ` · ${summary.coming} придут`
                    : " · перекличка в журнале"
              }`}
        </p>
      ) : null}

      {canMark && isCompetition ? (
        <p className={s.featuredSessionHint}>
          {(competitionSummary?.total ?? 0) > 0
            ? `${competitionSummary.approved} допущено${
                competitionSummary.pending > 0
                  ? ` · ${competitionSummary.pending} заявок`
                  : ""
              }`
            : "Заявок на соревнование пока нет"}
        </p>
      ) : null}

      <Button
        type="button"
        variant={past ? "secondary" : "primary"}
        onClick={onOpenJournal}
        className={s.featuredSessionJournalBtn}
      >
        {L.openJournal}
      </Button>

      {expectSheet ? (
        <ExpectationRespondentsSheet
          open={Boolean(expectSheetState)}
          onClose={() => setExpectSheetState(null)}
          title={expectSheetState?.title || ""}
          rows={expectSheetState?.rows || []}
          columnKey={expectSheetState?.columnKey}
        />
      ) : null}
    </article>
  );
}

export default function AdminOverview({ tenant, appMode = false }) {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const { profile, club, billing } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [setup, setSetup] = useState(null);
  const [sessionHidden, setSessionHidden] = useState(false);
  const setupComplete = isOnboardingSetupComplete(setup, tenant?.id);
  const showOnboarding =
    Boolean(tenant?.id) &&
    setup != null &&
    !sessionHidden &&
    !isOnboardingDismissed(tenant.id) &&
    !setupComplete;
  const today = toDateInputValue();
  const todayLabel = formatDateRu(`${today}T12:00:00`, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sportRes, setupRes] = await Promise.allSettled([
        clinicAPI.getSportToday(),
        clinicAPI.getClinicSetupStatus(),
      ]);
      setDash(sportRes.status === "fulfilled" ? sportRes.value : null);
      setSetup(setupRes.status === "fulfilled" ? setupRes.value : null);
    } catch (e) {
      toastRef.current.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useAdminRefresh(load);

  const nearestList =
    dash?.nearest_sessions?.length > 0
      ? dash.nearest_sessions
      : dash?.next_session
        ? [dash.next_session]
        : [];

  const openJournal = (sess) => {
    const date = sess?.session_date || today;
    if (!sess) {
      navigate(journalUrl({ date: today }));
      return;
    }
    navigate(
      journalUrl({
        date,
        sessionId: sess.id,
        groupId: sess.group?.id,
      }),
    );
  };

  const debtorsCount = dash?.debtors_count ?? 0;
  const sessionsToday = dash?.sessions || [];
  const notifications = dash?.notifications || [];
  const expectedToday = dash?.expected_clients_today ?? 0;
  const markedToday = sessionsToday.reduce(
    (sum, sess) => sum + (sess.present_count ?? 0),
    0,
  );
  const pollComingToday = dash?.poll_coming_today ?? 0;
  const hasPollToday = pollComingToday > 0 || (dash?.poll_sent_today ?? 0) > 0;

  const loadingView = appMode ? (
    <div className={aa.homeDashboard}>
      <SkeletonCard />
      <section className={aa.homeTodayZone}>
        <div className={aa.homeDayHead}>
          <h2>Сегодня</h2>
          <span>{todayLabel}</span>
        </div>
        <div className={aa.homeKpiStrip}>
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </section>
      <SkeletonCard />
    </div>
  ) : (
    <div className={s.summaryPage}>
      <div className={s.summaryKpiStrip}>
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
      <SkeletonCard />
    </div>
  );

  const kpiStrip = (
    <section
      className={appMode ? aa.homeKpiStrip : s.summaryKpiStrip}
      aria-label="Показатели дня"
    >
      <KpiCard
        accent
        appMode={appMode}
        icon={IconUsers}
        label="Клиентов ожидается"
        value={expectedToday}
        hint={
          hasPollToday
            ? `прогноз явки: ${pollComingToday} чел.`
            : markedToday > 0
              ? `${markedToday} уже отмечено в журнале`
              : "по всем занятиям сегодня"
        }
      />
      <KpiCard
        appMode={appMode}
        icon={IconCalendar}
        label="Занятий сегодня"
        value={sessionsToday.length}
        hint={
          sessionsToday.length
            ? nearestList[0]
              ? `ближайшее в ${formatTimeRu(nearestList[0].start_datetime)}`
              : "в расписании"
            : "добавьте расписание группы"
        }
      />
      <KpiCard
        appMode={appMode}
        icon={IconFinance}
        label="Выручка сегодня"
        value={fmtMoney(dash?.revenue_today)}
        hint={
          Number(dash?.revenue_today) > 0
            ? "принято за сегодня"
            : "платежи появятся после занятия"
        }
      />
    </section>
  );

  const forecastsBlock =
    dash?.forecasts ? (
      <SummaryForecasts forecasts={dash.forecasts} appMode={appMode} />
    ) : null;

  const birthdaysBlock =
    (dash?.birthdays_today || []).length > 0 ? (
      appMode ? (
        <section className={s.summaryBlock}>
          <h2 className={s.summaryBlockTitle}>Дни рождения</h2>
          <ul className={s.summaryBirthdayList}>
            {dash.birthdays_today.map((c) => (
              <li key={c.id}>
                <Link to={`/manage/app/clients/${c.id}`}>{c.full_name}</Link>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <section className={md.desktopBirthdays}>
          <h2 className={md.desktopBlockTitle}>Дни рождения</h2>
          <ul className={md.desktopBirthdayList}>
            {dash.birthdays_today.map((c) => (
              <li key={c.id}>
                <Link to={`/manage/app/clients/${c.id}`}>{c.full_name}</Link>
              </li>
            ))}
          </ul>
        </section>
      )
    ) : null;

  const attentionNotifications = filterAttentionNotifications(notifications);
  const pendingCompetitionApps = dash?.pending_competition_applications || [];
  const pendingRegistrations = dash?.pending_registrations || [];
  const pendingRegistrationsCount = dash?.pending_registrations_count ?? 0;

  const homeNearestSource =
    dash?.nearest_sessions?.length > 0
      ? dash.nearest_sessions
      : nearestList.length > 0
        ? nearestList
        : sessionsToday;

  const trialBanner = (
    <TrialSubscriptionBanner
      billing={billing}
      appMode={appMode}
      isOwner={club?.is_owner}
    />
  );

  const dashboard = appMode ? (
    <div className={aa.homeDashboard}>
      {trialBanner}
      <SummaryNotifications notifications={attentionNotifications} appMode />
      <SummaryPendingRegistrations
        registrations={pendingRegistrations}
        count={pendingRegistrationsCount}
        appMode
      />
      <SummaryCompetitionApplications
        applications={pendingCompetitionApps}
        appMode
      />
      <section className={aa.homeTodayZone} aria-label="Сегодня">
        <div className={aa.homeDayHead}>
          <h2>Сегодня</h2>
          <span>{todayLabel}</span>
        </div>
        {kpiStrip}
      </section>
      <SummaryNearestSessions
        sessions={homeNearestSource}
        navigate={navigate}
        profile={profile}
        club={club}
        openJournal={openJournal}
      />
      <SummaryQuickActions
        debtorsCount={debtorsCount}
        navigate={navigate}
        appMode
      />
      {forecastsBlock}
      <ClubLifeHomeSection
        feedLink="/manage/app/life"
        feedLabel="Лента"
        mode="admin"
        appMode
      />
      {birthdaysBlock}
    </div>
  ) : (
    <div className={`${s.summaryPage} ${md.desktopOverview}`}>
      {trialBanner}
      <SummaryNotifications notifications={attentionNotifications} appMode={false} />
      <SummaryPendingRegistrations
        registrations={pendingRegistrations}
        count={pendingRegistrationsCount}
        appMode={false}
      />
      <SummaryCompetitionApplications
        applications={pendingCompetitionApps}
        appMode={false}
      />

      <section className={md.desktopToday} aria-label="Сегодня">
        <div className={md.desktopTodayHead}>
          <h2 className={md.desktopTodayTitle}>Сегодня</h2>
          <span className={md.desktopTodayDate}>{todayLabel}</span>
        </div>
        {kpiStrip}
      </section>

      {forecastsBlock}

      <SummaryNearestSessions
        sessions={homeNearestSource}
        navigate={navigate}
        profile={profile}
        club={club}
        openJournal={openJournal}
        desktop
      />

      <div className={md.desktopOverviewGrid}>
        <div className={md.desktopOverviewMain}>
          <SummaryQuickActions
            debtorsCount={debtorsCount}
            navigate={navigate}
            appMode={false}
            desktop
          />
          <ClubLifeHomeSection
            feedLink="/manage/app/life"
            feedLabel="Лента"
            mode="admin"
            appMode={false}
            desktop
            limit={5}
          />
        </div>
        <aside className={md.desktopOverviewAside}>
          {birthdaysBlock}
        </aside>
      </div>
    </div>
  );

  if (showOnboarding) {
    return (
      <AdminOnboarding
        tenant={tenant}
        profile={profile}
        setup={setup}
        appMode={appMode}
        onRefresh={load}
        onDismiss={() => setSessionHidden(true)}
      >
        {loading ? loadingView : dashboard}
      </AdminOnboarding>
    );
  }

  if (loading) {
    return loadingView;
  }

  return dashboard;
}
